import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { getAllSponsors, createSponsor, updateSponsor, deleteSponsor } from "@/lib/api";

const tiers = ["gold", "silver", "bronze"];
const initialFormData = { name: "", logo_url: "", website_url: "", tier: "silver", order: 0, is_active: true };

export default function AdminSponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchSponsors = async () => {
    try {
      const response = await getAllSponsors();
      setSponsors(response.data);
    } catch (error) {
      console.error("Error fetching sponsors:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSponsors();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, tier: value });
  };

  const handleSwitchChange = (checked) => {
    setFormData({ ...formData, is_active: checked });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const data = { ...formData, order: parseInt(formData.order) };

    try {
      if (editingId) {
        await updateSponsor(editingId, data);
        toast.success("Sponsor bijgewerkt");
      } else {
        await createSponsor(data);
        toast.success("Sponsor aangemaakt");
      }
      setDialogOpen(false);
      setFormData(initialFormData);
      setEditingId(null);
      fetchSponsors();
    } catch (error) {
      toast.error("Fout bij opslaan sponsor");
      console.error("Error saving sponsor:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (sponsor) => {
    setFormData({
      name: sponsor.name,
      logo_url: sponsor.logo_url || "",
      website_url: sponsor.website_url || "",
      tier: sponsor.tier,
      order: sponsor.order.toString(),
      is_active: sponsor.is_active,
    });
    setEditingId(sponsor.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Weet je zeker dat je deze sponsor wilt verwijderen?")) return;

    try {
      await deleteSponsor(id);
      toast.success("Sponsor verwijderd");
      fetchSponsors();
    } catch (error) {
      toast.error("Fout bij verwijderen sponsor");
      console.error("Error deleting sponsor:", error);
    }
  };

  const openNewDialog = () => {
    setFormData(initialFormData);
    setEditingId(null);
    setDialogOpen(true);
  };

  const tierColors = {
    gold: "bg-amber-500 text-white",
    silver: "bg-slate-400 text-white",
    bronze: "bg-amber-700 text-white",
  };

  return (
    <div data-testid="admin-sponsors-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Sponsors</h1>
          <p className="text-slate-600">Beheer club sponsors en partners</p>
        </div>
        <Button onClick={openNewDialog} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-sponsor">
          <Plus className="h-4 w-4 mr-2" />
          Sponsor Toevoegen
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-slate-700">Volgorde</TableHead>
                <TableHead className="text-slate-700">Logo</TableHead>
                <TableHead className="text-slate-700">Naam</TableHead>
                <TableHead className="text-slate-700">Niveau</TableHead>
                <TableHead className="text-slate-700">Website</TableHead>
                <TableHead className="text-slate-700">Status</TableHead>
                <TableHead className="text-right text-slate-700">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-600">Laden...</TableCell>
                </TableRow>
              ) : sponsors.length > 0 ? (
                sponsors.map((sponsor) => (
                  <TableRow key={sponsor.id}>
                    <TableCell className="text-slate-600">{sponsor.order}</TableCell>
                    <TableCell>
                      {sponsor.logo_url ? (
                        <img src={sponsor.logo_url} alt={sponsor.name} className="h-10 w-auto object-contain" />
                      ) : (
                        <span className="text-slate-400">Geen logo</span>
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">{sponsor.name}</TableCell>
                    <TableCell>
                      <Badge className={tierColors[sponsor.tier]}>{sponsor.tier}</Badge>
                    </TableCell>
                    <TableCell>
                      {sponsor.website_url ? (
                        <a href={sponsor.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 flex items-center gap-1">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={sponsor.is_active ? "bg-green-500 text-white" : "bg-slate-400 text-white"}>
                        {sponsor.is_active ? "Actief" : "Inactief"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(sponsor)} className="text-slate-600 hover:text-slate-900">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(sponsor.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-600">Geen sponsors gevonden. Voeg je eerste sponsor toe!</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900">{editingId ? "Sponsor Bewerken" : "Sponsor Toevoegen"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-700">Naam</Label>
              <Input id="name" name="name" value={formData.name} onChange={handleChange} required className="border-slate-300" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="logo_url" className="text-slate-700">Logo URL</Label>
              <Input id="logo_url" name="logo_url" value={formData.logo_url} onChange={handleChange} placeholder="https://..." className="border-slate-300" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="website_url" className="text-slate-700">Website URL</Label>
              <Input id="website_url" name="website_url" value={formData.website_url} onChange={handleChange} placeholder="https://..." className="border-slate-300" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">Niveau</Label>
              <Select value={formData.tier} onValueChange={handleSelectChange}>
                <SelectTrigger className="border-slate-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tiers.map((tier) => (
                    <SelectItem key={tier} value={tier} className="capitalize">{tier === 'gold' ? 'Goud' : tier === 'silver' ? 'Zilver' : 'Brons'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="order" className="text-slate-700">Weergavevolgorde</Label>
              <Input id="order" name="order" type="number" min="0" value={formData.order} onChange={handleChange} className="border-slate-300" />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="is_active" className="text-slate-700">Actief</Label>
              <Switch id="is_active" checked={formData.is_active} onCheckedChange={handleSwitchChange} />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-300 text-slate-700">Annuleren</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? "Opslaan..." : "Opslaan"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
